import { unwrap } from 'solid-js/store'
import { backendAxios } from '../../../axios'
import { useEnginePrevStateSignal } from '../../../rx/signal/useEnginePrevStateSignal'
import { Nullable } from '../../../typescript'
import createDefaultWait from '../../createDefaultWait'
import { deepClone } from '../../deep_clone'
import { getEngineToast } from '../../three/fn/getSetEngineToast'
import { saveToDisk } from '../disk/DiskFactory'
import { Engine } from '../engine/Engine.type'
import { hasIndexedDB } from '../Storage/IndexedDB/hasIndexedDB'
import { EXPORT_DISK_PLACEHOLDER_ERROR, StateMapDisk } from './state_map_disk.enum'
import { encodePersistedStateBytes } from './persisted_state'
import { saveWorkspaceMap } from '../workspace/workspaceMapsClient'
import { encodeWorkspacePersistedStateBytes } from '../workspace/workspacePersistedState'

let lastPromise: Nullable<Promise<void>> = null

async function saveStateChangesInner(engine: Engine) {
  const [, setPrevState] = useEnginePrevStateSignal(engine)
  // for whatever reason, resist spamming the server
  const minimumWaitBetweenCalls = createDefaultWait(2000)
  const toast = getEngineToast(engine)
  try {

    const state = unwrap(engine.state)
    const mapId = state.map?.id
    if (!mapId) {
      throw new Error('Unexpected !mapId')
    }

    if (state.map.disk === StateMapDisk.Cloud) {
      await backendAxios.post(`/st1/maps/${mapId}`, { data: encodePersistedStateBytes(state) })

      setPrevState(deepClone(state))
      const msg = 'Map successfully saved.'
      if (toast) {
        toast.success(msg)
      } else {
        console.log(msg)
      }
    } else if (state.map.disk === StateMapDisk.IndexedDB) {
      if (!hasIndexedDB()) {
        throw new Error('IndexedDB requested but not available')
      }
      await saveToDisk(state, StateMapDisk.IndexedDB)
      setPrevState(deepClone(state))
    } else if (state.map.disk === StateMapDisk.Workspace) {
      if (!engine.workspaceMapEntry) {
        throw new Error('Workspace map entry is missing')
      }
      const workspaceMapEntry = deepClone(engine.workspaceMapEntry)
      workspaceMapEntry.row.data = encodeWorkspacePersistedStateBytes(state)
      await saveWorkspaceMap(workspaceMapEntry)
      setPrevState(deepClone(state))
    } else if (state.map.disk === StateMapDisk.Export) {
      const err = new Error(EXPORT_DISK_PLACEHOLDER_ERROR)
      if (toast) {
        toast.error(err.message)
      } else {
        throw err
      }
    } else {
      const err = new Error('unknown disk')
      if (toast) {
        toast.error(err.message)
      } else {
        throw err
      }
    }
  } catch (err) {
    console.error(err)
    if (toast) {
      toast.error(String(err))
    }
  } finally {
    await minimumWaitBetweenCalls
  }
}
export default async function saveStateChanges(engine: Engine) {
  const [getPrevState] = useEnginePrevStateSignal(engine)
  if (JSON.stringify(engine.state) === JSON.stringify(getPrevState())) {
    // console.log('no changes to save')
    return
  }
  await lastPromise
  lastPromise = saveStateChangesInner(engine)
  return lastPromise
}
